import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ImagesSelector } from './ImagesSelector';
import { Lightbox } from './Lightbox';
import { Media, MediaType } from '../../../codegen/graphql';

// Define the types for our context
export interface Image {
  type: MediaType;
  id: string;
  url: string;
  width?: number;
  height?: number;
}

interface ImageSelectorOptions {
  images: Image[];
  onSelectionComplete: (selectedImages: Image[]) => any;
  initialSelection?: Image[];
  singleSelect?: boolean;
}

interface LightboxOptions {
  medias: Image[];
  initialIndex?: number;
}

interface ImageContextType {
  openImageSelector: (options: ImageSelectorOptions) => any;
  openLightbox: (options: LightboxOptions) => any;
  closeImageSelector: () => void;
  closeLightbox: () => void;
}

// Create the context with default values
const ImageContext = createContext<ImageContextType>({
  openImageSelector: () => {},
  openLightbox: () => {},
  closeImageSelector: () => {},
  closeLightbox: () => {},
});

// Custom hook to use the image context
export const useImageTools = () => useContext(ImageContext);

interface ImageProviderProps {
  children: ReactNode;
}

export function ImageProvider({ children }: ImageProviderProps) {
  // State for the image selector
  const [imageSelectorOptions, setImageSelectorOptions] = useState<ImageSelectorOptions | null>(null);
  
  // State for the lightbox
  const [lightboxOptions, setLightboxOptions] = useState<LightboxOptions | null>(null);

  // Functions to open and close the image selector
  const openImageSelector = (options: ImageSelectorOptions) => {
    setImageSelectorOptions(options);
  };

  const closeImageSelector = () => {
    setImageSelectorOptions(null);
  };

  // Functions to open and close the lightbox
  const openLightbox = (options: LightboxOptions) => {
    setLightboxOptions(options);
  };

  const closeLightbox = () => {
    setLightboxOptions(null);
  };

  return (
    <ImageContext.Provider
      value={{
        openImageSelector,
        openLightbox,
        closeImageSelector,
        closeLightbox,
      }}
    >
      {children}

      {/* Render the image selector if options are provided */}
      {imageSelectorOptions && (
        <ImagesSelector
          images={imageSelectorOptions.images}
          onSelectionComplete={imageSelectorOptions.onSelectionComplete}
          onClose={closeImageSelector}
          initialSelection={imageSelectorOptions.initialSelection}
          singleSelect={imageSelectorOptions.singleSelect}
        />
      )}

      {/* Render the lightbox if options are provided */}
      {lightboxOptions && (
        <Lightbox
          medias={lightboxOptions.medias}
          initialIndex={lightboxOptions.initialIndex}
          onClose={closeLightbox}
        />
      )}
    </ImageContext.Provider>
  );
}
